import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaPaperPlane, FaBriefcase, FaExternalLinkAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { saveEnquiry, saveCareerApplication, getCompanyInfo } from '../firebase/db';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('contact');
  const [info, setInfo] = useState({
    name: 'Digvijay Industries',
    email: 'contact@digvijayindia.com',
    phone: '8956403388',
    address: 'J-79, MIDC, Kupwad, Sangli Maharashtra - 416436'
  });

  React.useEffect(() => {
    getCompanyInfo().then(data => {
      if (data) {
        if (data.phone) {
          data.phone = data.phone
            .split(',')
            .map(p => p.trim())
            .filter(p => !p.includes('2644484') && !p.includes('0233'))
            .join(', ') || '8956403388';
        }
        setInfo(data);
      }
    });
  }, []);

  // Contact Form State
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactStatus, setContactStatus] = useState('');

  // Career Form State
  const [careerForm, setCareerForm] = useState({ name: '', email: '', phone: '', position: '', experience: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeBase64, setResumeBase64] = useState('');
  const [careerStatus, setCareerStatus] = useState('');

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 700 * 1024) {
      Swal.fire('File Too Large', 'Resume size must be less than 700KB. Please compress your file and try again.', 'error');
      e.target.value = '';
      setResumeFile(null);
      setResumeBase64('');
      return;
    }
    setResumeFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setResumeBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactStatus('submitting');
    const success = await saveEnquiry({ ...contactForm, type: 'general', enquiryType: 'general' });
    if (success) {
      try {
        fetch('https://formsubmit.co/ajax/contact@digvijayindia.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            _subject: `New General Inquiry from ${contactForm.name}`,
            _template: 'table',
            'Full Name': contactForm.name,
            'Email Address': contactForm.email,
            'Phone Number': contactForm.phone,
            'Message': contactForm.message,
            'Submitted At': new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
          }),
        }).catch(err => console.error('Email send error:', err));
      } catch (err) {
        console.error('Try-catch block error:', err);
      }
      setContactStatus('success');
      setContactForm({ name: '', email: '', phone: '', message: '' });
      e.target.reset();
    } else {
      setContactStatus('error');
    }
    setTimeout(() => setContactStatus(''), 3000);
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      setCareerStatus('error');
      setTimeout(() => setCareerStatus(''), 3000);
      return;
    }
    setCareerStatus('submitting');
    const data = { ...careerForm, resume: resumeBase64, resumeName: resumeFile?.name || '' };
    const success = await saveCareerApplication(data);
    if (success) {
      try {
        fetch('https://formsubmit.co/ajax/contact@digvijayindia.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            _subject: `Career Application — ${careerForm.position} | ${careerForm.name}`,
            _template: 'table',
            'Full Name': careerForm.name,
            'Email Address': careerForm.email,
            'Phone Number': careerForm.phone,
            'Position Applied For': careerForm.position,
            'Years of Experience': careerForm.experience,
            'Resume File': resumeFile?.name || 'Not provided',
            'Submitted At': new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
          }),
        }).catch(err => console.error('Email send error:', err));
      } catch (err) {
        console.error('Try-catch block error:', err);
      }
      setCareerStatus('success');
      Swal.fire({
        title: 'Success!',
        text: t('formAppSuccess'),
        icon: 'success',
        confirmButtonColor: '#04416d'
      });
      setCareerForm({ name: '', email: '', phone: '', position: '', experience: '' });
      setResumeFile(null);
      setResumeBase64('');
      e.target.reset();
    } else {
      setCareerStatus('error');
      Swal.fire('Submission Failed', 'There was an error submitting your application. Please try again.', 'error');
    }
    setTimeout(() => setCareerStatus(''), 3000);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const displayPhone = (info.phone || '8956403388')
    .split(/[,/|]/)
    .map(p => p.trim())
    .filter(p => !p.includes('2644484') && !p.includes('0233') && p.length > 0)
    .join(', ') || '8956403388';

  return (
    <div className="contact-page">
      <div className="page-header">
        <div className="banner-particle bp-1" />
        <div className="banner-particle bp-2" />
        <div className="banner-particle bp-3" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="banner-badge">{t('contactBadge')}</span>
            <h1>{t('contactTitle1')} <span className="highlight-red">{t('contactTitle2')}</span></h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            {t('contactSubtitle')}
          </motion.p>
        </div>
      </div>

      <section className="section bg-light">
        <div className="container">
          <div className="contact-layout">
            {/* Contact Info Card */}
            <motion.div className="contact-info" variants={fadeInUp} initial="hidden" animate="visible">
              <div className="info-card glass">
                <span className="info-card-badge">{t('contactDirectComm')}</span>
                <h3>{t('contactInfoTitle')}</h3>
                <p className="info-card-subtitle">
                  {t('contactInfoSubtitle')}
                </p>

                <ul className="info-list">
                  <li className="info-item info-item-clickable">
                    <a
                      href="https://maps.app.goo.gl/niM11jhKVZrqEdcc7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="info-item-link-wrapper"
                      title="Open Factory & Office Address in Google Maps"
                    >
                      <div className="info-icon-wrap icon-address">
                        <FaMapMarkerAlt className="info-icon" />
                      </div>
                      <div className="info-item-text">
                        <span className="info-item-label">
                          {t('contactAddressLabel')}
                          <FaExternalLinkAlt className="info-ext-icon" />
                        </span>
                        <p>{info.address}</p>
                      </div>
                    </a>
                  </li>
                  <li className="info-item">
                    <div className="info-icon-wrap icon-phone">
                      <FaPhoneAlt className="info-icon" />
                    </div>
                    <div className="info-item-text">
                      <span className="info-item-label">{t('contactPhoneLabel')}</span>
                      <p>
                        <a href={`tel:${displayPhone.replace(/[^0-9+]/g, '')}`}>
                          {displayPhone}
                        </a>
                      </p>
                    </div>
                  </li>
                  <li className="info-item">
                    <div className="info-icon-wrap icon-email">
                      <FaEnvelope className="info-icon" />
                    </div>
                    <div className="info-item-text">
                      <span className="info-item-label">{t('contactEmailLabel')}</span>
                      <p>
                        <a href={`mailto:${info.email}`}>
                          {info.email}
                        </a>
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Forms Area */}
            <motion.div className="form-area glass" variants={fadeInUp} initial="hidden" animate="visible">
              <div className="form-tabs">
                <button
                  type="button"
                  className={`tab-btn ${activeTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setActiveTab('contact')}
                >
                  <FaPaperPlane /> {t('tabGeneralInquiry')}
                </button>
                <button
                  type="button"
                  className={`tab-btn ${activeTab === 'career' ? 'active' : ''}`}
                  onClick={() => setActiveTab('career')}
                >
                  <FaBriefcase /> {t('tabCareerApp')}
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === 'contact' && (
                <form onSubmit={handleContactSubmit} className="contact-form fade-in">
                  <div className="form-group">
                    <label className="form-label">{t('formFullName')}</label>
                    <input type="text" className="form-control" required placeholder={t('formEnterFullName')} value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label className="form-label">{t('formEmail')}</label>
                      <input type="email" className="form-control" required placeholder="name@example.com" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                    </div>
                    <div className="form-group half-width">
                      <label className="form-label">{t('formPhone')}</label>
                      <input type="tel" className="form-control" required placeholder={t('formEnterPhone')} value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('formMessage')}</label>
                    <textarea className="form-control" rows="4" required placeholder={t('formMessagePlaceholder')} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })}></textarea>
                  </div>
                  <button type="submit" className="btn btn-primary contact-submit-btn" disabled={contactStatus === 'submitting'}>
                    {contactStatus === 'submitting' ? t('formSending') : t('formSendMessage')}
                  </button>
                  {contactStatus === 'success' && <p className="success-msg">{t('formMsgSuccess')}</p>}
                </form>
              )}

              {/* Career Form */}
              {activeTab === 'career' && (
                <form onSubmit={handleCareerSubmit} className="career-form fade-in">
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label className="form-label">{t('formFullName')}</label>
                      <input type="text" className="form-control" required placeholder={t('formEnterFullName')} value={careerForm.name} onChange={e => setCareerForm({ ...careerForm, name: e.target.value })} />
                    </div>
                    <div className="form-group half-width">
                      <label className="form-label">{t('formEmail')}</label>
                      <input type="email" className="form-control" required placeholder="name@example.com" value={careerForm.email} onChange={e => setCareerForm({ ...careerForm, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label className="form-label">{t('formPhone')}</label>
                      <input type="tel" className="form-control" required placeholder={t('formEnterPhone')} value={careerForm.phone} onChange={e => setCareerForm({ ...careerForm, phone: e.target.value })} />
                    </div>
                    <div className="form-group half-width">
                      <label className="form-label">{t('formPosition')}</label>
                      <select className="form-control" required value={careerForm.position} onChange={e => setCareerForm({ ...careerForm, position: e.target.value })}>
                        <option value="">{t('formSelectPosition')}</option>
                        <option value="Mechanical Engineer">{t('posMechEng')}</option>
                        <option value="Technician">{t('posTech')}</option>
                        <option value="Sales Executive">{t('posSales')}</option>
                        <option value="Other">{t('posOther')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('formExperience')}</label>
                    <input type="number" className="form-control" required min="0" placeholder="e.g. 2" value={careerForm.experience} onChange={e => setCareerForm({ ...careerForm, experience: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('formUploadResume')}</label>
                    <input type="file" className="form-control file-input-styled" required accept=".pdf,.doc,.docx" onChange={handleResumeChange} />
                  </div>
                  <button type="submit" className="btn btn-secondary contact-submit-btn" disabled={careerStatus === 'submitting'}>
                    {careerStatus === 'submitting' ? t('formSubmitting') : t('formSubmitApp')}
                  </button>
                  {careerStatus === 'success' && <p className="success-msg">{t('formAppSuccess')}</p>}
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- GOOGLE MAP SECTION ---- */}
      <section className="map-section">
        <div className="map-header">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ textAlign: 'center', width: '100%' }}
            >
              <h2 className="map-title">{t('contactMapTitle')}</h2>
            </motion.div>
          </div>
        </div>
        <div className="map-container">
          <iframe
            title="Digvijay Industries Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3629.1109871147046!2d74.63356257492254!3d16.874726383927605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc122d9309f6a4b%3A0xa9e0ce5c8756dbab!2sDigvijay%20Industries!5e0!3m2!1sen!2sin!4v1789117864959!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </section>

    </div>
  );
};

export default Contact;
